<template>
  <div class="vacancy-step-testing__q-container">
    <div v-if="$vuetify.breakpoint.smAndDown">
      <div class="d-flex">
        <div class="vacancy-step-testing__q-id">
          {{ $t('question') }} № {{ idx }}
        </div>
        <TestQuestionControls
          :question="question"
          :last-question-order="lastQuestionOrder"
          @deleteQuestion="$emit('deleteQuestion', $event)"
          @changeQuestionOrderUp="$emit('changeQuestionOrderUp', $event)"
          @changeQuestionOrderDown="$emit('changeQuestionOrderDown', $event)"
        />
      </div>
      <VDivider class="mt-2 mb-3" />
      <VRadioGroup v-model="question.type">
        <VRadio
          v-for="(tab, index) in tabs"
          :key="index"
          :value="tab.val"
          :label="tab.name"
          :class="{ 'd-none': !tab.show }"
          class="mb-4"
        />
      </VRadioGroup>
    </div>
    <VTabs
      v-else
      :value="tabValue"
      @change="changeTab($event)"
      fixed-tabs
    >
      <div class="vacancy-step-testing__q-id">
        {{ $t('question') }} № {{ idx }}
      </div>
      <VTab
        v-for="(tab, index) in tabs"
        :key="index"
        :href="`#${tab.val}`"
        :class="{ 'd-none': !tab.show }"
      >
        <span class="vacancy-step-testing__q-name">{{ tab.name }}</span>
      </VTab>
      <TestQuestionControls
        :question="question"
        :last-question-order="lastQuestionOrder"
        @deleteQuestion="$emit('deleteQuestion', $event)"
        @changeQuestionOrderUp="$emit('changeQuestionOrderUp', $event)"
        @changeQuestionOrderDown="$emit('changeQuestionOrderDown', $event)"
      />
    </Vtabs>
    <VTabsItems v-model="tabValue">
      <VTabItem
        v-for="(tab, index) in tabs"
        :key="index"
        :value="tab.val"
        :transition="false"
        :reverse-transition="false"
      >
        <div>
          <VTextarea
            v-model="question.question"
            outlined
            dense
            required
            rows="3"
            :label="$t('questionTextPlaceholder')"
            :counter="255"
            :error-messages="questionErrorMessages"
            @input="saveQuestion()"
          />

          <div class="vacancy-step-testing__q-controls-block">
            <div class="vacancy-step-testing__add-image">
              <input
                id="fileInput"
                type="file"
                accept="image/png, image/gif, image/jpeg"
                @change="loadImg($event)"
              />

              <div class="vacancy-step-testing__add-image-real">
                <img :src="require('@/assets/img/test/add-image.svg')">

                <span v-if="hasImage">
                  {{ $t('addAnother') }}
                </span>

                <span v-else>
                  {{ $t('addImage') }}
                </span>
              </div>
            </div>

            <div class="vacancy-step-testing__points">
              <div class="vacancy-step-testing__points-block">
                {{ $t('pointsForQuestion') }}: <span @click="openInputScore()">{{ question.score }}</span>
              </div>
              <template v-if="showScoreInput">
                <input
                  ref="scoreInput"
                  class="mt-1"
                  style="max-width: 80px;"
                  oninput="validity.valid||(value='');"
                  type="number"
                  placeholder="0"
                  min="0"
                  max="1000"
                  :value="question.score"
                  @input="changeInputScore($event)"
                  @keydown.enter.prevent
                  @focusout="() => showScoreInput = false"
                >
                <img @click="showScoreInput = false" :src="require('@/assets/img/test/check.svg')" alt="check">
              </template>
            </div>
          </div>

          <VImg
            v-if="hasImage"
            :lazy-src="imageSrc"
            :src="imageSrc"
            class="vacancy-step-testing__add-image_preview"
            @click="openDialogZoomImage()"
          >
            <div v-if="imageIsLoading" class="overlay">
              <Preloader />
            </div>
            <div v-else class="vacancy-step-testing__add-image_trash" @click.stop="deleteImage()">
              <img :src="require('@/assets/img/test/image-trash.svg')" alt="trash">
            </div>
          </VImg>

          <div>
            <TestQuestionSelectType
              v-if="isQuestionTypeSelect"
              :answers="question.answers"
              :show-validation-messages-global="showValidationMessagesGlobal"
              @changeAnswers="changeAnswers"
              @initialChangeAnswers="initialChangeAnswers"
              @changeQuestionType="changeQuestionType"
              @initialChangeQuestionType="initialChangeQuestionType"
            />
            <TestQuestionTextType
              v-else-if="isQuestionTypeText"
              :question-type="question.type"
              :answers="question.answers"
              :show-validation-messages-global="showValidationMessagesGlobal"
              @changeAnswers="changeAnswers"
              @initialChangeAnswers="initialChangeAnswers"
              @changeQuestionType="changeQuestionType"
            />
            <TestQuestionNumericType
              v-else-if="isQuestionTypeNumeric"
              :answers="question.answers"
              :show-validation-messages-global="showValidationMessagesGlobal"
              @initialChangeAnswers="initialChangeAnswers"
              @changeAnswers="changeAnswers"
            />
          </div>

          <VDialog
            v-model="dialogZoomImage"
            width="675"
            :retain-focus="false"
            overlay-color="black"
            overlay-opacity="0.1"
            content-class="elevation-0"
          >
            <div class="dialog-img-block">
              <VImg
                v-if="hasImage"
                :src="imageSrc"
              />
              <VBtn
                icon
                small
                class="close-button"
                @click="closeDialogZoomImage()"
              >
                <img :src="require('@/assets/img/test/close.svg')" alt="close">
              </VBtn>
            </div>
          </VDialog>
        </div>
      </VTabItem>
    </VTabsItems>
  </div>
</template>

<script>
import TestQuestionControls from '@/components/Test/TestQuestionControls.vue';
import TestQuestionSelectType from '@/components/Test/TestQuestionSelectType.vue';
import TestQuestionTextType from '@/components/Test/TestQuestionTextType.vue';
import TestQuestionNumericType from '@/components/Test/TestQuestionNumericType.vue';
import {
  toRefs, ref, computed, onMounted, watch, nextTick,
} from '@vue/composition-api';
import { useI18n } from 'vue-i18n-composable';
import { testQuestionTypes } from '@/enums';
import Preloader from '../Preloader.vue';

export default {
  name: 'TestQuestion',
  components: {
    Preloader,
    TestQuestionControls,
    TestQuestionSelectType,
    TestQuestionTextType,
    TestQuestionNumericType,
  },
  props: {
    question: {
      type: Object,
      required: true,
    },
    idx: {
      type: Number,
      required: true,
    },
    showValidationMessagesGlobal: {
      type: Boolean,
      required: true,
    },
    lastQuestionOrder: {
      type: Number,
      required: true,
    },
  },
  setup(props, { emit }) {
    const { question, showValidationMessagesGlobal, lastQuestionOrder } = toRefs(props);

    const { t } = useI18n();

    const scoreInput = ref(null);

    const showScoreInput = ref(false);

    const tabs = ref([
      {
        name: t('selectChoice'),
        val: testQuestionTypes.SELECT,
        show: true,
      },
      // {
      //   name: t('selectChoice'),
      //   val: testQuestionTypes.MULTISELECT,
      //   show: false,
      // },
      {
        name: t('numericalChoice'),
        val: testQuestionTypes.NUMERIC,
        show: true,
      },
      // {
      //   name: t('textChoice'),
      //   val: testQuestionTypes.TEXT,
      //   show: false,
      // },
      {
        name: t('textChoice'),
        val: testQuestionTypes.FREE,
        show: true,
      },
    ]);

    const dialogZoomImage = ref(false);
    const questionImage = ref(null);
    const imageIsLoading = ref(false);
    const uploadedImg = ref(question.value.uploadedImg || null);
    const showValidationMessages = ref(false);

    const isQuestionTypeSelect = computed(() => question.value.type === testQuestionTypes.SELECT || question.value.type === testQuestionTypes.MULTISELECT);
    const isQuestionTypeText = computed(() => question.value.type === testQuestionTypes.TEXT || question.value.type === testQuestionTypes.FREE);
    const isQuestionTypeNumeric = computed(() => question.value.type === testQuestionTypes.NUMERIC);

    const isEmptyQuestion = computed(() => !question.value.question || !question.value.question.trim());
    const isEmptyQuestionByUser = computed(() => question.value.question === '');
    const isQuestionTooLong = computed(() => question?.value?.question?.length > 255);
    const questionErrorMessages = computed(() => {
      const errors = [];

      if (isEmptyQuestion.value && (showValidationMessages.value || isEmptyQuestionByUser.value)) {
        errors.push(t('requiredField'));
      }

      if (isQuestionTooLong.value) {
        errors.push(`${t('charactersLimit')} - ${255}`);
      }

      return errors;
    });

    const isLastQuestion = computed(() => lastQuestionOrder.value === question.value.order);

    const isAnswersInvalid = computed(() => {
      if (question.value.type === testQuestionTypes.FREE) {
        return false;
      }

      return question.value.answers.filter((a) => !a.isValid).length > 0;
    });

    const isValid = computed(() => !(isAnswersInvalid.value || isEmptyQuestion.value || isQuestionTooLong.value));

    const setQuestionImage = computed(
      () => (uploadedImg.value ? URL.createObjectURL(uploadedImg.value) : null),
    );
    const hasImage = computed(() => !!question.value.image?.url || !!uploadedImg.value);
    const imageSrc = computed(() => setQuestionImage.value || question.value.image?.url);

    const tabValue = computed(() => {
      switch (question.value.type) {
        case testQuestionTypes.TEXT:
        case testQuestionTypes.FREE: {
          return testQuestionTypes.FREE;
        }
        case testQuestionTypes.NUMERIC: {
          return testQuestionTypes.NUMERIC;
        }
        case testQuestionTypes.SELECT:
        case testQuestionTypes.MULTISELECT:
        {
          return testQuestionTypes.SELECT;
        }
        default: {
          return '';
        }
      }
    });

    function saveQuestion() {
      question.value.isValid = isValid.value;
      emit('saveQuestion', question.value, uploadedImg.value);
    }

    function openInputScore() {
      question.value.score = !!question.value.score || question.value.score === 0
        ? question.value.score
        : 0;
      showScoreInput.value = true;
      nextTick(() => {
        if (!scoreInput) {
          return;
        }

        const input = Array.isArray(scoreInput.value) ? scoreInput.value[0] : scoreInput.value;
        input.focus();
      });
    }

    function changeInputScore(event) {
      question.value.score = event.target.value;
      saveQuestion();
    }

    function openDialogZoomImage() {
      dialogZoomImage.value = true;
    }

    function closeDialogZoomImage() {
      dialogZoomImage.value = false;
    }

    function deleteImage() {
      if (question.value.image) {
        emit('deleteImage', question.value.id);
      }
      uploadedImg.value = null;
    }

    function changeAnswers(currentAnswers) {
      question.value.answers = currentAnswers;
      saveQuestion();
    }

    function initialChangeAnswers(currentAnswers) {
      question.value.answers = currentAnswers;
    }

    function initialChangeQuestionType(qType) {
      question.value.type = qType;
    }

    function changeQuestionType(qType) {
      question.value.type = qType;
      saveQuestion();
    }

    function loadImg(e) {
      uploadedImg.value = e.target.files[0];
      question.value.uploadedImg = uploadedImg.value;
      saveQuestion();
      imageIsLoading.value = true;
    }

    onMounted(() => {
      question.value.isValid = isValid.value;
      document.getElementById('fileInput').addEventListener('change', (event) => { this.submit(event); });
      // saveQuestion();
    });

    watch(showValidationMessagesGlobal, (flag) => {
      if (!flag) {
        return;
      }

      showValidationMessages.value = true;
    });

    function changeTab(val) {
      question.value.answers = question.value.answers.map((q) => {
        return {
          id: q.id,
          answer: q.answer,
          isCorrect: q.isCorrect,
        };
      });
      changeQuestionType(val);
    }

    watch(question, () => {
      if (question.value.image && imageIsLoading.value) {
        imageIsLoading.value = false;
        uploadedImg.value = null;
      }
    }, { deep: true });

    return {
      tabs,
      saveQuestion,
      openInputScore,
      changeInputScore,
      closeDialogZoomImage,
      openDialogZoomImage,
      dialogZoomImage,
      questionImage,
      uploadedImg,
      deleteImage,
      changeAnswers,
      isQuestionTypeSelect,
      isQuestionTypeText,
      isQuestionTypeNumeric,
      testQuestionTypes,
      changeQuestionType,
      questionErrorMessages,
      hasImage,
      imageSrc,
      isLastQuestion,
      changeTab,
      loadImg,
      tabValue,
      showScoreInput,
      scoreInput,
      imageIsLoading,
      initialChangeAnswers,
      initialChangeQuestionType,
    };
  },
};
</script>

<style scoped lang="scss">
.vacancy-step-testing {
  &__q-container {
    width: 100%;
    margin-bottom: 10px;
  }
  &__q-id {
    font-size: 18px;
    line-height: 28px;
    color: black;
    width: 100%;
  }
  &__q-name {
    color: #333333;
    font-size: 14px;
    line-height: 20px;
    letter-spacing: 0.25px;
  }
  &__q-controls-block {
    display: flex;
    align-items: flex-start;
    @media screen and (max-width: 599px) {
      margin-bottom: 15px;
    }
  }
  &__q {
    &_radio {
      display: flex;
      width: 100%;
      flex-direction: column;
      margin-bottom: 8px;
      input {
        position: relative;
        width: 100%;
      }
    }
    &_radio-block {
      width: 100%;
      display: flex;
      @media screen and (max-width: 599px) {
        flex-wrap: wrap;
        margin-bottom: 5px;
      }
    }
    &_radio-answer {
      max-width: 320px;
    }
    &_radio-answer-input {
      @media screen and (max-width: 599px) {
        width: 80%;
      }
    }
    &_radio-answer-controls {
      margin: 10px 0 0 8px;
      order: 2;
      @media screen and (max-width: 599px) {
        order: 1;
      }
    }
    &_radio-answer-checkbox {
      order: 1;
      margin: 4px 0 0 8px;
      @media screen and (max-width: 599px) {
        order: 2;
        margin: 0;
      }
    }
    &_radio-points {
      width: 80px;
      margin-left: 8px;
    }
    &_trash {
      margin-left: 20px;
      cursor: pointer;
      @media screen and (max-width: 599px) {
        margin-left: 10px;
      }
    }
  }
  &__add-image {
    margin: 0 0 -5px;
    position: relative;
    min-width: 180px;
    input {
      max-width: 190px;
      opacity: 0;
      height: 60px;
      padding: 0 !important;
      cursor: pointer;
    }
    &_preview {
      max-width: 330px;
      margin-bottom: 15px;
      position: relative;
      cursor: zoom-in;
    }
    &_trash {
      position: absolute;
      top: 5px;
      right: 5px;
      padding: 0 6px;
      background-color: #EA0D0D;
      border-radius: 50%;
      cursor: pointer
    }
  }
  &__points {
    position: absolute;
    left: 200px;
    display: flex;
    img {
      cursor: pointer;
      margin-left: 5px;
      width: 25px;
    }
    @media screen and (max-width: 599px) {
      left: 0;
      top: 123px;
    }
  }
  &__points-block {
    border-bottom: 1px dashed #0A82D9;
    display: flex;
    margin-top: 15px;
    font-size: 14px;
    color: #0A82D9;
    height: 24px;
    margin-right: 10px;
    span {
      cursor: pointer;
      margin-left: 5px;
    }
  }
  &__add-image-real {
    position: absolute;
    top: 15px;
    cursor: pointer;
    pointer-events: none;
    border-bottom: 1px dashed #0A82D9;
    display: flex;
    align-items: center;
    @media screen and (max-width: 599px) {
      top: 30px;
    }
    span {
      color: #0A82D9;
      font-size: 14px;
      margin-left: 5px;
      height: 24px;
    }
  }
}

.overlay {
  height: 100%;
  backdrop-filter: blur(2px);
}

.answer-block-radio {
  flex-direction: column-reverse;
}
.answer-block {
  display: flex;
  width: 75%;
  @media screen and (max-width: 900px) {
    width: 100%;
  }
}
.dialog-img-block {
  position: relative;
  padding: 50px;
  @media screen and (max-width: 599px) {
    padding: 0;
    position: absolute;
    left: 0;
    top: 50%;
    transform: translate(0, -50%);
    display: flex;
    align-items: center;
    height: 100%;
  }
}
.close-button {
  position: absolute;
  right: 15px;
  top: 15px;
}

input {
  border: 1px solid #C2C2C2;
  border-radius: 4px;
  padding: 6px 12px;
  background-color: #fff;
  outline: none;
}

.v-tabs::v-deep {
  .v-tabs-bar {
    margin-bottom: 30px;
  }
}

.v-image {
  background-color: #fff;
}

.v-radio::v-deep {
  .v-label {
    color: #333333 !important;
  }
}

.v-input--checkbox::v-deep {
  .v-input__control {
    .v-input__slot {
      margin-bottom: 0;
      .v-input--selection-controls__input {
        margin-right: 3px;
      }
    }
    .v-messages {
      display: none;
    }
  }
}

.error-message {
  color: #ff5252;
  font-size: 12px;
}
</style>

<i18n locale="en" lang="yaml">
  questionTextPlaceholder: Question text*
  addImage: Add an image
  addAnother: Change the image
  pointsForQuestion: Points per question
  charactersLimit: The maximum number of characters is
  requiredField: This field is required
  ok: Ok
  question: Question
  selectChoice: multiple choice
  numericalChoice: numerical answer
  textChoice: fill in the blank
</i18n>

<i18n locale="ru" lang="yaml">
  questionTextPlaceholder: Текст вопроса*
  addImage: Добавьте изображение
  addAnother: Изменить изображение
  pointsForQuestion: Баллы за вопрос
  charactersLimit: Максимальное количество символов
  requiredField: Обязательное поле для заполнения
  ok: Ок
  question: Вопрос
  selectChoice: с вариантами ответов
  numericalChoice: с числовым ответом
  textChoice: с текстовым ответом
</i18n>
